// @flow
import { isUserAccountShown } from '@site-builder/common/src/utils/login';

import type { HeaderBlock } from '@site-builder/common/src/types/block/header/header-block';
import type { FiveSymbolLocale } from '@site-builder/common/src/types/locale';
import type { WidgetsType, Authorization } from '@site-builder/common/src/types/model/landing';

import { UserAccountService } from "../../../scripts/blocks/user-account-services/user-account-service";
import { showHeaderLineMenu } from '../../../scripts/landingAPI/showHeaderLineMenu';
import {canShowCart, PlaceTypes} from "../../../utils/cart-helper";



export const LayoutEnum = {
  BURGER: 'burger',
  LINE: 'line'
};

export type ILayoutEnum = $Values<typeof LayoutEnum>;

export class HeaderLayout {
  static MOBILE_BREAK_POINT = 575;

  static TABLE_BREAK_POINT = 810;

  static LIMIT_ITEMS = 4; // after which burger menu is always shown

  static LIMIT_ITEMS_WITH_USER_ACCOUNT = this.LIMIT_ITEMS - 1; // after which burger menu is always shown

  static get hasDocument() {
    return typeof document !== 'undefined';
  }

  static _getDeviceWidth() {
    return (this.hasDocument && document.documentElement?.clientWidth) || NaN;
  }

  static _sumHeaderItems({
    block,
    auth,
    widgets,
    localeList,
    isMultipage,
    isLandingIncludeStoreBlock,
    isNewScheme,
    enableCart,
  }: {
    block: HeaderBlock,
    auth: Authorization,
    widgets: WidgetsType,
    localeList: FiveSymbolLocale[],
    isMultipage: boolean,
    isLandingIncludeStoreBlock: boolean,
    isNewScheme: boolean,
    enableCart: boolean,
  }): number {
    return block.components.length
      + canShowCart({
        isMultipage, isLandingIncludeStoreBlock, isNewScheme, enableCart, placeType: PlaceTypes.HEADER,
      })
      + this.isShowLocaleSelector({ widgets, localeList })
      + block.values.logo.enable
      + !!isUserAccountShown(auth);
  }

  static isShowLocaleSelector({
    widgets,
    localeList
  }: {|
    widgets: WidgetsType,
    localeList: FiveSymbolLocale[]
  |}): boolean {
    return ['header', 'both'].includes(widgets.translationsLocation) && localeList.length > 1;
  }

  static isShowUserAccountInLineHeader({
    block,
    userService
  }: {
    block: HeaderBlock,
    userService: UserAccountService
  }): boolean {
    const isMobileScreen = this._getDeviceWidth() <= this.MOBILE_BREAK_POINT;
    if (isMobileScreen) {
      return false;
    }

    const isUnauthorizedUser = !userService?.isAuthorized;
    if (isUnauthorizedUser) {
      return true;
    }

    const balanceUnits = (userService?.balance || []).filter(currency => !!currency.amount).length;
    const isEmptyBalance = !balanceUnits;
    const isCurrenciesFitToLineHeader = balanceUnits * 136 <= this._getDeviceWidth() - 48 - 80 - 148;
    const isDisableLogo = !block.values.logo.enable;
    return (isEmptyBalance || isCurrenciesFitToLineHeader) && isDisableLogo;
  }

  static compute({
    block,
    auth,
    widgets,
    localeList,
    isMultipage,
    isLandingIncludeStoreBlock,
    userService,
    isNewScheme,
    enableCart
  }: {
    block: HeaderBlock,
    auth: Authorization,
    widgets: WidgetsType,
    localeList: FiveSymbolLocale[],
    isMultipage: boolean,
    isLandingIncludeStoreBlock: boolean,
    userService: UserAccountService,
    isNewScheme: boolean,
    enableCart: boolean,
  }): ILayoutEnum {
    const headerItems = this._sumHeaderItems({
      block,
      auth,
      widgets,
      localeList,
      isMultipage,
      isLandingIncludeStoreBlock,
      isNewScheme,
      enableCart
    });

    const isTooManyItems = headerItems > this.LIMIT_ITEMS;
    const isMobileScreen = this._getDeviceWidth() <= this.MOBILE_BREAK_POINT;
    const isTableScreenAndAuthorizedUser = this._getDeviceWidth() <= this.TABLE_BREAK_POINT
      && userService?.isAuthorized;

    if (showHeaderLineMenu()) {
      return LayoutEnum.LINE;
    }

    if (isTooManyItems || isMobileScreen || isTableScreenAndAuthorizedUser) {
      return LayoutEnum.BURGER;
    }

    return LayoutEnum.LINE;
  }
}
